import { delegate } from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/shift-away.css";

/**
 * Badge tooltips (T212588).
 *
 * One delegated tippy instance on <body> serves EVERY badge on the page, server-rendered or
 * React-rendered. That matters for three reasons:
 *
 *  1. The acceptance criterion is "click or tap". Hover-only tooltips are unreachable on
 *     touch devices, so the trigger is `click` — tippy synthesises that from a tap.
 *  2. React re-renders and AJAX-loaded collection lists replace badge nodes at will.
 *     Delegation means new badges work with no re-initialisation and no observers.
 *  3. The React bundle needs no tippy import of its own, so the badge cannot drift into two
 *     tooltip behaviours.
 *
 * The tooltip is appended to <body> so it is never clipped by a card's `overflow: hidden`.
 */

export const BADGE_TOOLTIP_SELECTOR = "[data-badge-tooltip]";

export function badgeTooltipOptions() {
  return {
    // Click on pointer devices, tap on touch. Never hover-only.
    trigger: "click",
    allowHTML: true,
    interactive: true,
    theme: "valspar-badge",
    animation: "shift-away",
    placement: "top",
    offset: [0, 8],
    appendTo: () => document.body,
    maxWidth: 260,
    zIndex: 9999,
    // Keep the tooltip reachable for keyboard users who tab onto the trigger.
    touch: true,
    onCreate(instance) {
      instance.reference.setAttribute("aria-expanded", "false");
    },
    onShow(instance) {
      const content = instance.reference.getAttribute("data-badge-tooltip");

      if (!content) {
        return false;
      }

      instance.setContent(content);
      instance.reference.setAttribute("aria-expanded", "true");

      // aria-describedby is what makes the explanation reach screen-reader users who
      // activate the badge, rather than relying on the visual tooltip alone.
      instance.reference.setAttribute("aria-describedby", instance.popper.id);

      return true;
    },
    onHide(instance) {
      instance.reference.setAttribute("aria-expanded", "false");
      instance.reference.removeAttribute("aria-describedby");
    },
  };
}

let initialised = false;

export default function initBadgeTooltips() {
  if (initialised || typeof document === "undefined") {
    return;
  }

  initialised = true;

  const start = () => {
    delegate(document.body, {
      target: BADGE_TOOLTIP_SELECTOR,
      ...badgeTooltipOptions(),
      // On touch the tooltip should also dismiss when tapping elsewhere, which is tippy's
      // default for click triggers; spelled out here so it is not lost in a future edit.
      hideOnClick: true,
    });
  };

  if (document.readyState !== "loading") {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start);
  }
}
